import { useModal } from "@/Contexts";
import { useTerminalsStateRE } from "@/States/Terminals";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { useCallback } from "react";

import moment from "moment";
import { twMerge } from "tailwind-merge";

import type { NewsfeedPost } from "../types";
import { DeleteMessageModal } from "./deleteMessageModal";

type Props = {
  newsfeedPost: NewsfeedPost;
  setEditMode: (value: boolean) => void;
  deleteMessage: ({
    terminalId,
    deletePost,
  }: {
    terminalId: string;
    deletePost: boolean;
  }) => void;
};

export const MessageView: React.FC<Props> = ({
  newsfeedPost,
  setEditMode,
  deleteMessage,
}: Props) => {
  const { t } = useTrans();
  const { showModal, hideModal } = useModal();
  const { currentTerminal } = useTerminalsStateRE();

  const onDelete = useCallback(async () => {
    showModal(
      <DeleteMessageModal
        onConfirm={() => {
          deleteMessage({
            terminalId: currentTerminal.id,
            deletePost: true,
          });
        }}
        onCancel={hideModal}
      />,
    );
  }, [deleteMessage, currentTerminal, showModal, hideModal]);

  const expandImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      showModal(
        <div
          className="relative flex h-full w-full cursor-pointer items-center justify-center rounded-[6px] p-[100px]"
          onMouseDown={(e) => e.currentTarget === e.target && hideModal()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <img src={newsfeedPost?.imageUrl} />
        </div>,
      );
    },
    [showModal, hideModal, newsfeedPost],
  );

  return (
    <div className="flex h-full w-full">
      <div className="w-full">
        <div className="text-[11.5px] text-[#9a958f]">
          {t("libraryPage:messageUpdated")}{" "}
          {moment(newsfeedPost.updatedAt).format("DD.MM.YYYY")}
        </div>
        <div className="dmSans mt-[7px] text-[19px] leading-[1.3] font-bold">
          {newsfeedPost.title}
        </div>
        <div className="mt-[8px] min-h-[200px] text-[14px] leading-[1.55] whitespace-pre-wrap text-[#4a4747]">
          {newsfeedPost.message}
        </div>
        <div className="mt-4 flex w-full flex-row gap-[10px]">
          <button
            type="button"
            className={twMerge(
              "inline-flex cursor-pointer items-center gap-[9px] bg-black px-[22px] py-[9px] text-[13px] font-semibold text-white hover:bg-[#3a3838]",
              CLIPPED_BUTTON_STYLE,
            )}
            onClick={() => setEditMode(true)}
          >
            {t("libraryPage:editMessageButton")}
          </button>
          <button
            type="button"
            className={twMerge(
              "text-tomato inline-flex cursor-pointer items-center gap-[9px] bg-transparent px-[4px] py-[9px] text-[13px] font-medium underline",
              CLIPPED_BUTTON_STYLE,
            )}
            onClick={onDelete}
          >
            {t("libraryPage:deleteMessageButton")}
          </button>
        </div>
      </div>
      {newsfeedPost?.imageUrl && (
        <div className="mt-[18px] min-h-[310px] w-[460px]">
          <div
            className="h-full w-[460px] cursor-pointer rounded-[6px]"
            style={{
              backgroundImage: `url(${newsfeedPost?.imageUrl})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
            }}
            onClick={expandImage}
          />
        </div>
      )}
    </div>
  );
};
